<template>
  <div ref="root" class="lg-menu" @keydown="onKeydown">
    <div class="lg-menu-trigger" @click="toggle">
      <slot name="trigger" :open="open" :toggle="toggle" />
    </div>

    <ul
      v-if="open"
      ref="listEl"
      class="lg-menu-list"
      :class="`align-${align}`"
      role="menu"
      tabindex="-1"
    >
      <li v-for="(item, i) in items" :key="item.value" role="none">
        <button
          type="button"
          class="lg-menu-item"
          :class="{ active: i === activeIndex, danger: item.danger }"
          role="menuitem"
          :disabled="item.disabled"
          @click="choose(item)"
          @mousemove="activeIndex = i"
        >
          <component :is="item.icon" v-if="item.icon" :size="15" aria-hidden="true" />
          <span>{{ item.label }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount, nextTick } from 'vue';

export interface MenuItem {
  label: string;
  value: string;
  icon?: unknown;
  danger?: boolean;
  disabled?: boolean;
}

const props = withDefaults(
  defineProps<{
    items: MenuItem[];
    align?: 'left' | 'right';
  }>(),
  { align: 'left' },
);

const emit = defineEmits<{ select: [value: string] }>();

const open = ref(false);
const activeIndex = ref(-1);
const root = ref<HTMLElement | null>(null);
const listEl = ref<HTMLElement | null>(null);

function toggle() {
  open.value ? close() : openMenu();
}
function openMenu() {
  activeIndex.value = -1;
  open.value = true;
  nextTick(() => listEl.value?.focus());
}
function close(refocus = false) {
  open.value = false;
  if (refocus) {
    const t = root.value?.querySelector<HTMLElement>('.lg-menu-trigger *');
    t?.focus();
  }
}
function choose(item: MenuItem) {
  if (item.disabled) return;
  emit('select', item.value);
  close();
}
function move(delta: number) {
  const n = props.items.length;
  if (!n) return;
  let i = activeIndex.value;
  for (let step = 0; step < n; step++) {
    i = (i + delta + n) % n;
    if (!props.items[i].disabled) break;
  }
  activeIndex.value = i;
}

function onKeydown(e: KeyboardEvent) {
  if (!open.value) return;
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      move(1);
      break;
    case 'ArrowUp':
      e.preventDefault();
      move(-1);
      break;
    case 'Enter':
    case ' ':
      if (activeIndex.value >= 0) {
        e.preventDefault();
        choose(props.items[activeIndex.value]);
      }
      break;
    case 'Escape':
      e.preventDefault();
      close(true);
      break;
    case 'Tab':
      close();
      break;
  }
}

function onDocPointer(e: MouseEvent) {
  if (open.value && root.value && !root.value.contains(e.target as Node)) close();
}
document.addEventListener('pointerdown', onDocPointer);
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocPointer));
</script>

<style scoped>
.lg-menu {
  position: relative;
  display: inline-flex;
}
.lg-menu-trigger {
  display: inline-flex;
}
.lg-menu-list {
  position: absolute;
  z-index: var(--lg-z-menu);
  top: calc(100% + var(--lg-space-1));
  min-width: 180px;
  margin: 0;
  padding: var(--lg-space-1);
  list-style: none;
  border-radius: var(--lg-radius-md);
  border: 1px solid var(--lg-border);
  background: var(--lg-bg-raised);
  box-shadow: var(--lg-shadow-2);
}
.lg-menu-list:focus {
  outline: none;
}
.align-left {
  left: 0;
}
.align-right {
  right: 0;
}
.lg-menu-item {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  width: 100%;
  min-height: var(--lg-control-md);
  padding: 0 var(--lg-space-3);
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text);
  font-family: var(--lg-font-sans);
  font-size: var(--lg-fs-md);
  text-align: left;
  cursor: pointer;
}
.lg-menu-item:disabled {
  opacity: 0.5;
  cursor: default;
}
.lg-menu-item.active:not(:disabled) {
  background: var(--lg-bg-hover);
}
.lg-menu-item.danger {
  color: var(--lg-danger);
}
</style>
